import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api, { formatError } from "../lib/api";
import LmsCourseSelect from "../components/LmsCourseSelect";
import { useAuth } from "../contexts/AuthContext";
import { UploadCloud, FileText, Sparkles, Loader2, Cpu, BookOpenCheck, Plus, X, GraduationCap, Settings, Lock } from "lucide-react";

export default function CreateRattrapagePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [module, setModule] = useState("");
  const [aiModel, setAiModel] = useState("gpt-5.2");
  const [models, setModels] = useState([]);
  const [syllabus, setSyllabus] = useState(null);
  const [courseSource, setCourseSource] = useState("upload");
  const [courseFiles, setCourseFiles] = useState([]);
  const [lmsCourses, setLmsCourses] = useState([]);
  const [lmsStatus, setLmsStatus] = useState("loading"); // loading | enabled | disabled | error
  const [examplesCount, setExamplesCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [m, ex] = await Promise.all([api.get("/models"), api.get("/examples")]);
        setModels(m.data);
        setExamplesCount(ex.data.length);
      } catch {}
      try {
        const { data } = await api.get("/lms/status");
        setLmsStatus(data?.enabled ? "enabled" : "disabled");
      } catch {
        setLmsStatus("error");
      }
    })();
  }, []);

  const lmsEnabled = lmsStatus === "enabled";

  const courseReady = courseSource === "lms" ? lmsCourses.length > 0 : courseFiles.length > 0;
  const canSubmit = module.trim() && syllabus && courseReady && aiModel && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!canSubmit) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("module", module.trim());
      fd.append("ai_model", aiModel);
      fd.append("syllabus", syllabus);
      fd.append("course_source", courseSource);
      if (courseSource === "lms") {
        fd.append("lms_course_ids", lmsCourses.map((c) => c.id).join("\n"));
      } else {
        for (const f of courseFiles) {
          fd.append("course", f);
        }
      }
      const { data } = await api.post("/rattrapages", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      navigate(`/rattrapage/${data.id}`);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="px-8 lg:px-12 py-10 max-w-[1100px]" data-testid="create-page">
        <div className="label-xs mb-2">Nouveau rattrapage</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Générer un QCM de rattrapage
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">
          Renseignez le module, déposez le syllabus et le cours. L'IA s'inspirera également de votre bibliothèque
          d'exemples ({examplesCount}) pour caler le style et la difficulté.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-8" data-testid="create-form">
          {/* Module */}
          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="label-xs mb-3">1. Module concerné</div>
            <input
              type="text"
              required
              value={module}
              onChange={(e) => setModule(e.target.value)}
              placeholder="Ex. Mécanique des fluides — Promo 2026 — Groupe A"
              data-testid="create-module-input"
              className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-sm bg-white focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
            />
          </section>

          {/* File uploads */}
          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="label-xs mb-3">2. Documents du module</div>

            <FileSlot
              label="Syllabus (1 fichier, obligatoire)"
              file={syllabus}
              onSelect={setSyllabus}
              testId="syllabus"
              hint="Programme officiel, objectifs pédagogiques"
            />

            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="label-xs">Contenu du cours</div>
                <div className="inline-flex items-center bg-slate-100 rounded-sm p-0.5" data-testid="course-source-toggle">
                  <SourceBtn
                    active={courseSource === "upload"}
                    onClick={() => { setCourseSource("upload"); setLmsCourses([]); }}
                    testId="course-source-upload"
                  >
                    <UploadCloud size={13} /> Uploader des fichiers
                  </SourceBtn>
                  <SourceBtn
                    active={courseSource === "lms"}
                    onClick={() => setCourseSource("lms")}
                    testId="course-source-lms"
                  >
                    {lmsEnabled ? <GraduationCap size={13} /> : <Lock size={13} />} Depuis LearnWorlds
                  </SourceBtn>
                </div>
              </div>

              {courseSource === "lms" ? (
                lmsEnabled ? (
                  <>
                    <LmsCourseSelect selected={lmsCourses} onChange={setLmsCourses} />
                    <div className="mt-3 text-xs text-slate-500">
                      Le contenu des cours sélectionnés (sections, unités, descriptions) est récupéré automatiquement
                      via l'API LearnWorlds au moment de la génération.
                    </div>
                  </>
                ) : (
                  <div
                    className="border border-dashed border-slate-300 bg-slate-50 rounded-sm px-5 py-6 text-center"
                    data-testid="lms-not-configured"
                  >
                    <Lock size={18} className="mx-auto text-slate-400" />
                    <div className="mt-2 text-sm font-semibold text-slate-800">
                      {lmsStatus === "error"
                        ? "État de l'intégration LearnWorlds indisponible"
                        : lmsStatus === "loading"
                        ? "Vérification de l'intégration LearnWorlds…"
                        : "L'intégration LearnWorlds n'est pas encore configurée"}
                    </div>
                    <p className="mt-1 text-xs text-slate-600 max-w-md mx-auto">
                      {lmsStatus === "error"
                        ? "Le serveur n'a pas répondu. Rechargez la page ; si le problème persiste, contactez un super-administrateur."
                        : lmsStatus === "loading"
                        ? "Un instant, nous interrogeons le serveur."
                        : user?.role === "superadmin"
                        ? "Renseignez le domaine de l'école, le Client ID et le Client Secret LearnWorlds, puis activez l'intégration pour piocher les cours directement dans le LMS."
                        : "Demandez à un super-administrateur d'activer l'intégration LearnWorlds dans la configuration système."}
                    </p>
                    {lmsStatus === "disabled" && user?.role === "superadmin" && (
                      <Link
                        to="/admin/configuration"
                        data-testid="lms-configure-link"
                        className="mt-4 inline-flex items-center gap-1.5 bg-[#0f172a] text-white px-4 py-2 rounded-sm text-xs font-semibold hover:bg-slate-800"
                      >
                        <Settings size={13} /> Configurer LearnWorlds
                      </Link>
                    )}
                  </div>
                )
              ) : (
                <>
                  <MultiFileSlot
                    label="Cours (plusieurs fichiers possibles)"
                    files={courseFiles}
                    onAdd={(fs) => setCourseFiles((prev) => [...prev, ...fs])}
                    onRemove={(idx) => setCourseFiles((prev) => prev.filter((_, i) => i !== idx))}
                    testId="course"
                    hint="Tous les chapitres / modules du cours"
                  />
                  <div className="mt-3 text-xs text-slate-500">
                    Formats acceptés : PDF, DOCX, TXT. 25 Mo maximum par fichier, 80 Mo au total pour les cours.
                  </div>
                </>
              )}
            </div>
          </section>

          {/* AI model */}
          <section className="bg-white border border-slate-200 rounded-md p-6">
            <div className="label-xs mb-3">3. Modèle d'intelligence artificielle</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {models.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setAiModel(m.key)}
                  data-testid={`model-${m.key}`}
                  className={`text-left border rounded-sm p-4 transition-colors ${
                    aiModel === m.key
                      ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900"
                      : "border-slate-200 hover:border-slate-400 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Cpu size={14} className="text-[#2563eb]" />
                    <span className="font-semibold text-sm text-slate-900">{m.label}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1.5">{m.description}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Examples summary */}
          <section className="bg-white border border-slate-200 rounded-md p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center shrink-0">
              <BookOpenCheck size={18} className="text-slate-700" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">
                {examplesCount > 0 ? `${examplesCount} exemple(s) de référence utilisés` : "Aucun exemple de référence"}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                {examplesCount > 0
                  ? "L'IA s'inspirera de votre bibliothèque pour caler le format et la difficulté."
                  : "Pour de meilleurs résultats, ajoutez des exemples passés depuis la bibliothèque."}
              </div>
            </div>
          </section>

          {err && (
            <div className="text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="create-error">
              {err}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">
              4 questions · 15 min/question · 1h total · ne pas afficher la correction au répondant.
            </div>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="generate-qcm-btn"
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-sm font-semibold text-sm transition-all ${
                canSubmit
                  ? "bg-[#2563eb] text-white hover:bg-blue-700"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {busy ? "Génération en cours…" : "Générer le QCM"}
            </button>
          </div>

          {busy && (
            <div className="tracing-beam rounded-md p-6 reveal" data-testid="generating-state">
              <div className="flex items-center gap-3">
                <Loader2 className="animate-spin text-[#2563eb]" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">L'IA conçoit votre rattrapage…</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Analyse du syllabus, du cours et des exemples. Cela peut prendre 30 à 90 secondes.
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </AppShell>
  );
}

function SourceBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-white"
      }`}
    >
      {children}
    </button>
  );
}

function FileSlot({ label, file, onSelect, testId, hint }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onSelect(f);
      }}
      onClick={() => inputRef.current?.click()}
      className={`upload-zone ${drag ? "dragging" : ""} rounded-sm p-5 cursor-pointer`}
      data-testid={`upload-${testId}-zone`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.txt,.md"
        className="hidden"
        onChange={(e) => onSelect(e.target.files?.[0] || null)}
        data-testid={`upload-${testId}-input`}
      />
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm bg-white border border-slate-200 flex items-center justify-center shrink-0">
          {file ? <FileText size={16} className="text-[#2563eb]" /> : <UploadCloud size={16} className="text-slate-500" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-xs">{label}</div>
          {file ? (
            <>
              <div className="text-sm font-semibold text-slate-900 mt-1 truncate" data-testid={`upload-${testId}-name`}>{file.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{(file.size / 1024).toFixed(1)} Ko · cliquez pour changer</div>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold text-slate-900 mt-1">Glissez ou cliquez pour ajouter</div>
              <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MultiFileSlot({ label, files, onAdd, onRemove, testId, hint }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (arr.length) onAdd(arr);
  };

  return (
    <div data-testid={`upload-${testId}-wrapper`}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`upload-zone ${drag ? "dragging" : ""} rounded-sm p-5 cursor-pointer`}
        data-testid={`upload-${testId}-zone`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.md"
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid={`upload-${testId}-input`}
        />
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-sm bg-white border border-slate-200 flex items-center justify-center shrink-0">
            {files.length > 0 ? <FileText size={16} className="text-[#2563eb]" /> : <UploadCloud size={16} className="text-slate-500" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="label-xs">{label}</div>
            <div className="text-sm font-semibold text-slate-900 mt-1">
              {files.length === 0
                ? "Glissez ou cliquez pour ajouter"
                : `${files.length} fichier${files.length > 1 ? "s" : ""} sélectionné${files.length > 1 ? "s" : ""}`}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
          </div>
          <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563eb] mt-1">
            <Plus size={12} /> Ajouter
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid={`upload-${testId}-list`}>
          {files.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-sm text-xs"
              data-testid={`upload-${testId}-item-${idx}`}
            >
              <FileText size={12} className="text-slate-400 shrink-0" />
              <span className="flex-1 truncate text-slate-800">{f.name}</span>
              <span className="text-slate-400">{(f.size / 1024).toFixed(0)} Ko</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
                className="text-slate-400 hover:text-[#b91c1c]"
                title="Retirer"
                data-testid={`upload-${testId}-remove-${idx}`}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

